import { QuestionContentFlag } from '@games/word-clash/shared/types';

/**
 * Which language's content is worth showing for a question.
 *
 * `en-only` / `tr-only` are not decoration — they decide which language's
 * matches a question is served in, so the OTHER language's answers, hints,
 * aliases and special/plausible-wrong lists are dead data that will never
 * reach a player. Rendering them anyway made every admin surface ambiguous:
 * an `en-only` row showed an empty (or worse, half-authored and stale)
 * Turkish column that reviewers could not distinguish from "Turkish content
 * still to be written".
 *
 * Every question surface (list card, review card, quality table, editor)
 * MUST derive its language columns from this helper rather than re-reading
 * `contentFlags` inline, so they cannot drift into disagreeing about what an
 * `en-only` question is.
 */
export interface QuestionLanguageVisibility {
  showEnglish: boolean;
  showTurkish: boolean;
}

export function questionLanguageVisibility(
  contentFlags: readonly QuestionContentFlag[] | undefined,
): QuestionLanguageVisibility {
  const flags = contentFlags ?? [];
  const enOnly = flags.includes(QuestionContentFlag.EnOnly);
  const trOnly = flags.includes(QuestionContentFlag.TrOnly);

  // Both flags set is contradictory data (the server does not reject it).
  // Fail OPEN — show everything — rather than hiding both languages and
  // leaving an admin staring at a question with no content at all.
  if (enOnly && trOnly) {
    return { showEnglish: true, showTurkish: true };
  }

  return { showEnglish: !trOnly, showTurkish: !enOnly };
}
